import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const rgsPreset = definePreset(Aura, {
  semantic: {
    typography: {
      fontSize: '1rem',
    },
    primary: {
      '50': '#f5f9ff',
      '100': '#d0e1fd',
      '200': '#abc9fb',
      '300': '#85b2f9',
      '400': '#609af8',
      '500': '#3b82f6',
      '600': '#326fd1',
      '700': '#295bac',
      '800': '#204887',
      '900': '#183462',
      '950': '#0f213e',
    },
  },
  css: (options) => {
    const dt = options!.dt;
    const addonRadius = dt('inputgroup.addon.border.radius');

    return `
    /* --- p-inputgroup interop -------------------------------------------------
       PrimeNG only reaches inputs through wrappers it knows about, as a *direct*
       child of .p-inputgroup (.p-component, .p-iconfield, .p-floatlabel, ...).
       Two shapes we use fall outside that list and so keep their standalone
       border-radius, breaking up the group:
         - our <app-rgs-input-number> host, and
         - <p-floatlabel> wrapping a <p-iconfield> (needed to float a label over
           an input that also carries an icon).
       These rules give both the same treatment PrimeNG gives its own wrappers. */

    .p-inputgroup > app-rgs-input-number > .p-floatlabel > .p-component,
    .p-inputgroup > .p-floatlabel > .p-iconfield > .p-component,
    .p-inputgroup > .p-iftalabel > .p-iconfield > .p-component {
        border-radius: 0;
        margin: 0;
    }

    .p-inputgroup > app-rgs-input-number:first-child > .p-floatlabel > .p-component,
    .p-inputgroup > .p-floatlabel:first-child > .p-iconfield > .p-component,
    .p-inputgroup > .p-iftalabel:first-child > .p-iconfield > .p-component {
        border-start-start-radius: ${addonRadius};
        border-end-start-radius: ${addonRadius};
    }

    .p-inputgroup > app-rgs-input-number:last-child > .p-floatlabel > .p-component,
    .p-inputgroup > .p-floatlabel:last-child > .p-iconfield > .p-component,
    .p-inputgroup > .p-iftalabel:last-child > .p-iconfield > .p-component {
        border-start-end-radius: ${addonRadius};
        border-end-end-radius: ${addonRadius};
    }

    /* A focused input is given z-index 1 inside an input group, and a flex item
       honours z-index even while position:static - so it paints over the
       absolutely positioned icon that precedes it, hiding the icon on focus. */
    .p-inputgroup .p-inputicon {
        z-index: 2;
    }

    /* Same story for the floating label: PrimeNG lifts it over a focused input
       with '.p-inputwrapper ~ label', which never matches when the input sits
       inside a p-iconfield instead of an inputwrapper. */
    .p-inputgroup .p-floatlabel .p-iconfield ~ label,
    .p-inputgroup .p-iftalabel .p-iconfield ~ label {
        z-index: 1;
    }
    `;
  },
  components: {
    menu: {
      /* Aura leans on surface-400 for menu icons, which sits at about 2.4:1 on
         white - the icons read as disabled. Darken icons and labels a step;
         non-text contrast wants 3:1 and surface-600 clears 7:1. */
      item: {
        color: '{surface.800}',
        focusColor: '{surface.900}',
        icon: {
          color: '{surface.600}',
          focusColor: '{surface.900}',
        },
      },
      submenuIcon: {
        color: '{surface.600}',
        focusColor: '{surface.900}',
      },
      css: `
        /* --- topbar profile menu --------------------------------------------
           Scoped through [styleClass], which PrimeNG merges onto the overlay
           root (cn(cx('root'), styleClass())), so none of this leaks to the
           other menus in the app. */

        /* Item badges are laid out inline right after the label; only
           .p-menu-item-submenu-icon gets an auto margin from the base sheet.
           Excluding toggleable rows keeps a future badge on a group header
           from fighting the chevron for the same space. */
        .topbar-profile-menu .p-menu-item:not(.p-menu-item-toggleable) .p-menu-item-link > .p-badge {
          margin-inline-start: auto;
        }

        /* The overlay is positioned once, on open, and never re-aligned. Cap
           the license list so expanding it cannot push the menu past the
           viewport, which would make absolutePosition flip it above the
           trigger. overscroll-behavior stops a scroll at the end of the list
           from chaining to the page - page scroll closes the overlay. */
        .topbar-profile-menu .p-menu-item-toggleable > .p-menu-submenu-list {
          max-height: 40vh;
          overflow-y: auto;
          overscroll-behavior: contain;
        }

        /* Font Awesome glyphs are wider than the 1rem box Aura reserves. */
        .topbar-profile-menu .p-menu-item-icon {
          width: 1.25rem;
          text-align: center;
        }

        /* The active license carries item.disabled so it cannot be re-selected
           and drops out of arrow-key nav, but it is the *current* choice rather
           than an unavailable one - the global '.p-disabled { opacity: .6 }'
           washed it out. Restore full opacity and keep the rest of what
           disabled buys us (pointer-events: none). */
        .topbar-profile-menu .topbar-license-active.p-disabled {
          opacity: 1;
        }

        /* highlight.* is PrimeNG's own selected-item pair and resolves to
           primary-700 on primary-50: ~6.2:1, where primary-color (primary-500)
           only managed ~3.5:1 and failed AA. */
        .topbar-profile-menu .topbar-license-active > .p-menu-item-content {
          background: var(--p-highlight-background);
        }

        .topbar-profile-menu .topbar-license-active > .p-menu-item-content .p-menu-item-icon,
        .topbar-profile-menu .topbar-license-active > .p-menu-item-content .p-menu-item-label {
          color: var(--p-highlight-color);
          font-weight: 600;
        }
      `,
    },
    tag: {
      css: `
        .p-tag-label {
          font-size: 0.85rem;
        }
      `,
    },
    tabs: {
      css: `
        .p-tabpanels {
          padding-top: 1.5rem;
          padding-left: 0;
          padding-right: 0;
        }
      `,
    },
    slider: {
      track: {
        background: 'rgba(15, 23, 42, 0.25)',
      },
      range: {
        background: '#0F172A',
      },
      handle: {
        background: '#FFFFFF',
        hoverBackground: '#F8FAFC',
        content: {
          background: '#0F172A',
          hoverBackground: '#1E293B',
        },
        focusRing: {
          color: '#2563EB',
        },
      },
    },
    confirmdialog: {
      css: () => `
        div.p-dialog-footer {
          display: flex !important;
          justify-content: space-between !important;
        }
      `,
    },
  },
});
